import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Refund = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Refund & Return Policy" : t("footer.legal.refund")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            <section>
              <h2>1. Overview</h2>
              <p className="text-base leading-relaxed mb-4">
                At Optivra Tech, we are committed to delivering high-quality IT services. This Refund & Return Policy outlines the conditions under which refunds may be provided for our digital services.
              </p>
              <p className="text-base leading-relaxed">
                Please note that our services are digital in nature and customized to each client's requirements. Therefore, refunds are handled on a case-by-case basis in accordance with this policy.
              </p>
            </section>

            <section>
              <h2>2. Refund Eligibility</h2>
              
              <h3>2.1 Full Refund Eligibility</h3>
              <p className="text-base leading-relaxed mb-4">
                You are eligible for a full refund if:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>You request a refund within 7 days of initial payment</li>
                <li>No project work has been initiated</li>
                <li>No project planning or discovery sessions have been conducted</li>
                <li>No resources have been allocated to your project</li>
              </ul>

              <h3>2.2 Partial Refund Eligibility</h3>
              <p className="text-base leading-relaxed mb-4">
                You may be eligible for a partial refund if:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Work has been initiated but not completed</li>
                <li>You wish to cancel the project mid-way</li>
                <li>We are unable to deliver the agreed services due to technical limitations</li>
              </ul>
              <p className="text-base leading-relaxed">
                In such cases, you will be charged only for the work completed and resources utilized. The remaining amount will be refunded.
              </p>

              <h3>2.3 No Refund Situations</h3>
              <p className="text-base leading-relaxed mb-4">
                Refunds will NOT be provided in the following situations:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Project milestones have been approved and delivered</li>
                <li>Completed digital deliverables have been handed over to you</li>
                <li>Refund request is made more than 7 days after milestone approval</li>
                <li>Cancellation is due to reasons beyond our control</li>
                <li>You have not provided required inputs or feedback despite reminders</li>
                <li>Change of mind after project commencement</li>
              </ul>
            </section>

            <section>
              <h2>3. Refund Process</h2>
              
              <h3>3.1 How to Request a Refund</h3>
              <p className="text-base leading-relaxed mb-4">
                To request a refund:
              </p>
              <ol className="space-y-2 ml-4 mb-4">
                <li>Email us at founder@optivratech.com with "Refund Request" in the subject line</li>
                <li>Include your order details, payment information, and reason for refund</li>
                <li>Our team will review your request within 2-3 business days</li>
                <li>You will receive a response via email with the refund decision</li>
              </ol>

              <h3>3.2 Refund Timeline</h3>
              <p className="text-base leading-relaxed mb-4">
                Once your refund is approved:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Processing time: 3-5 business days</li>
                <li>Bank transfer/payment gateway: 7-14 business days</li>
                <li>Total expected time: Up to 14 business days from approval</li>
              </ul>
            </section>

            <section>
              <h2>4. Service-Specific Policies</h2>
              
              <h3>4.1 Custom Development Services</h3>
              <p className="text-base leading-relaxed mb-4">
                For custom website development, AI automation, and similar services:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Refunds are calculated based on completed milestones</li>
                <li>Once a milestone is approved, payment for that milestone is non-refundable</li>
                <li>Source code is only provided after full payment</li>
              </ul>

              <h3>4.2 Consulting Services</h3>
              <p className="text-base leading-relaxed mb-4">
                For consulting and advisory services:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Once a consulting session is conducted, fees are non-refundable</li>
                <li>Unused session credits may be transferred to future services</li>
              </ul>

              <h3>4.3 Support & Maintenance Packages</h3>
              <p className="text-base leading-relaxed mb-4">
                For ongoing support services:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Pro-rated refunds available for unused months</li>
                <li>Minimum 30-day notice required for cancellation</li>
                <li>No refund for services already utilized</li>
              </ul>
            </section>

            <section>
              <h2>5. Quality Guarantee</h2>
              <p className="text-base leading-relaxed mb-4">
                We stand behind the quality of our work. If you're not satisfied with our deliverables:
              </p>
              <ul className="space-y-2 ml-4">
                <li>We offer revisions as per the project agreement</li>
                <li>We will work with you to address concerns before considering refunds</li>
                <li>We maintain open communication throughout the project</li>
              </ul>
            </section>

            <section>
              <h2>6. Disputed Charges</h2>
              <p className="text-base leading-relaxed mb-4">
                If you believe you have been charged incorrectly:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Contact us immediately at founder@optivratech.com</li>
                <li>Provide details of the disputed charge</li>
                <li>We will investigate and respond within 5 business days</li>
              </ul>
              <p className="text-base leading-relaxed">
                Please contact us before initiating a chargeback with your bank, as this may affect our ability to serve you in the future.
              </p>
            </section>

            <section>
              <h2>7. Exceptions & Special Cases</h2>
              <p className="text-base leading-relaxed mb-4">
                In exceptional circumstances, we may consider refunds outside of this policy. This includes:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Severe technical issues preventing project completion</li>
                <li>Mutual agreement to terminate the project</li>
                <li>Extraordinary situations beyond either party's control</li>
              </ul>
            </section>

            <section>
              <h2>8. Refund Method</h2>
              <p className="text-base leading-relaxed mb-4">
                Refunds will be processed using the same payment method used for the original transaction:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Credit/Debit Card: Refund to original card</li>
                <li>Bank Transfer: Refund to provided bank account</li>
                <li>Online Payment Gateway: Refund through gateway to source account</li>
              </ul>
            </section>

            <section>
              <h2>9. Contact for Refund Queries</h2>
              <p className="text-base leading-relaxed mb-2">
                For any questions or concerns regarding refunds:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">GST: 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2">Address: C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base mb-2">Phone: +91 86198 38718</p>
                <p className="text-base">Response Time: Within 24-48 hours</p>
              </div>
            </section>

            <section>
              <h2>10. Policy Updates</h2>
              <p className="text-base leading-relaxed">
                We reserve the right to update this Refund Policy at any time. Changes will be posted on this page with an updated "Last Updated" date. Your continued use of our services after changes indicates acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Refund;
